import { useId } from 'react';
import { ItemPersonalization } from '../types';
import { formatPrice } from '../utils/formatPrice';

interface Props {
  instructions?: string | null;
  fee?: number | null;
  charLimit?: number | null;
  placements?: string[];
  value: ItemPersonalization;
  onChange: (p: ItemPersonalization) => void;
  label?: string;
  onRemove?: () => void;
  textError?: string | null;
  placementError?: string | null;
}

export function ItemPersonalizer({ instructions, fee, charLimit, placements, value, onChange, label, onRemove, textError, placementError }: Props) {
  const uid = useId();
  const text = value?.text ?? '';
  const placement = value?.placement ?? '';
  const charCount = text.length;

  const handleTextChange = (newText: string) => {
    onChange({ text: newText, placement: value?.placement, fee: fee ?? undefined });
  };

  const handlePlacementChange = (newPlacement: string) => {
    onChange({ text, placement: newPlacement, fee: fee ?? undefined });
  };

  return (
    <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-blue-900">
          {label ?? 'Personalization'}
        </h3>
        <div className="flex items-center gap-2">
          {fee != null && fee > 0 && (
            <span className="text-sm font-medium text-blue-700">+${formatPrice(fee)}</span>
          )}
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="text-xs text-red-500 hover:text-red-700 font-medium"
            >
              Remove
            </button>
          )}
        </div>
      </div>

      {instructions && (
        <p className="text-xs text-blue-700 mb-3 italic">{instructions}</p>
      )}

      <div className="mb-2">
        <label className="block text-xs font-medium text-gray-700 mb-1">
          Personalization Text <span className="text-red-600">*</span>
        </label>
        <textarea
          value={text}
          onChange={(e) => handleTextChange(e.target.value)}
          rows={2}
          maxLength={charLimit ?? undefined}
          className={`w-full text-sm border bg-white rounded-md px-3 py-2 focus:outline-none focus:ring-2 resize-none ${textError ? 'border-red-400 focus:ring-red-400' : 'border-gray-300 focus:ring-blue-500'}`}
          placeholder="Enter personalization text..."
        />
        {textError && (
          <p className="text-xs mt-1 text-red-600">{textError}</p>
        )}
        {charLimit != null && (
          <p className="text-xs mt-1 text-right text-gray-500">
            {charCount} / {charLimit} characters
          </p>
        )}
      </div>

      {Array.isArray(placements) && placements.length > 0 && (
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Placement <span className="text-red-600">*</span>
          </label>
          <div className="flex flex-wrap gap-3">
            {placements.map((p) => (
              <label key={p} className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="radio"
                  name={`personalization-placement-${uid}`}
                  value={p}
                  checked={placement === p}
                  onChange={() => handlePlacementChange(p)}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">{p}</span>
              </label>
            ))}
          </div>
          {placementError && (
            <p className="text-xs mt-1 text-red-600">{placementError}</p>
          )}
        </div>
      )}
    </div>
  );
}
